import Debugs from 'oc-threejs/Debugs';
import Maths from "oc-commons/Maths";
import PixelAccessor from "./PixelAccessor";

/**
 * Statische Utility-Klasse zum Thema Image und Canvas
 */
export default class Images {

    /**
     * erzeugt ein Canvas von einem Image
     * @param image
     */
    static canvasFromImage(image,width=undefined,height=undefined) {
        if (image==null) {
            return image;
        }
        width = width===undefined ? image.width : width;
        height = height===undefined ? image.height : height;
        const canvas = Images.canvas(width,height);
        const ctx = canvas.getContext("2d");
        ctx.drawImage( image, 0, 0, width, height);
        return canvas;
    }

    /**
     * erzeugt ein Image von einem Canvas
     */
    static convertCanvasToImage(canvas) {
        const image = new Image();
        image.src = canvas.toDataURL("image/png");
        return image;
    }

    /**
     * erzeugt ein Canvas
     */
    static canvas(width,height) {
        const canvas = document.createElement('canvas');
        canvas.setAttribute('width', width);
        canvas.setAttribute('height', height);
        return canvas;
    }

    /**
     * lädt ein Image mit einem Promise
     * @returns {Promise}
     */
    static loadImage(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.addEventListener('load', e => resolve(img));
            img.addEventListener('error', () => {
                reject(new Error(`Failed to load image's URL: ${url}`));
            });
            img.src = url;
        });
    }

    /**
     * prüft, ob ein object ein Canvas ist
     */
    static isCanvas(object) {
        return object!=null && object.drawImage!==undefined;
    }

    /**
     * Malt einen Shadow um die nicht-transparenten Teile eines Bildes
     */
    static dropShadow(image, blur=20, x=2, y=2, color="rgba( 0, 0, 0, 1 )") {
        if (!image) {
            return image;
        }

        const canvas = Images.canvas(image.width,image.height);
        const ctx = canvas.getContext("2d");
        ctx.shadowColor = color;
        ctx.shadowOffsetX = x;
        ctx.shadowOffsetY = y;
        ctx.shadowBlur = blur;
        ctx.drawImage( image, 0, 0 );

        return canvas;
    }

    /**
     * Malt ein transparentes Bild über einen einfarbigen Hintergrund
     */
    static overUnicolorBackground(image,backgroundColor="rgb(255,255,255)") {
        if (!image) {
            return image;
        }
        const canvas = Images.canvas(image.width,image.height);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle=backgroundColor;
        ctx.fillRect(0,0,image.width,image.height);
        ctx.drawImage( image, 0, 0 );

        return canvas;
    }

    /**
     * verkleinert ein Bild
     */
    static scaleDown(image,sizeFactor) {
        if (!image) {
            return image;
        }
        const w = Math.round(image.width*sizeFactor);
        const h = Math.round(image.width*sizeFactor);
        const canvas = Images.canvas(w,h);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, w, h);
        return canvas;
    }

    /**
     * prüft, ob ein Canvas transparente Pixel enthält
     */
    static hasTransparency(canvas, minOpacity=255) {
        const ctx = canvas.getContext('2d');
        const imgdata = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const length = imgdata.length / 4;
        for (let i = 0; i < length; ++i) {
            if (imgdata[i * 4 + 3] < minOpacity) {
                return true;
            }
        }
        return false;
    }

    /**
     * gibt den tatsächlich verwendeten Farbbereich des Canvas zurück als min- und max- Color (je Int[4] entspricht RGBA)
     * Kann benutzt werden, um die Einfarbigkeit eines Images oder einzelner Farbkanäle zu prüfen.
     */
    static getMinMaxRGBA(canvas) {
        const pixels = new PixelAccessor(canvas);
        const min = pixels.createRGBA().map(()=>Number.POSITIVE_INFINITY);
        const max = pixels.createRGBA().map(()=>Number.NEGATIVE_INFINITY);
        const rgba = pixels.createRGBA();
        pixels.forRaster((x,y)=>{
            pixels.getRGBA(x,y,rgba);
            for (let i=0;i<4;i++) {
                min[i] = Math.min(min[i],rgba[i]);
                max[i] = Math.max(max[i],rgba[i]);
            }
        });
        return {min,max};
    }

    /**
     *
     * @param img
     * @returns {boolean}
     */
    static isImageLoaded(img) {
        if (img==null) {
            return false;
        }

        // During the onload event, IE correctly identifies any images that
        // weren’t downloaded as not complete. Others should too. Gecko-based
        // browsers act like NS4 in that they report this incorrectly.
        if (!img.complete) {
            return false;
        }

        // However, they do have two very useful properties: naturalWidth and
        // naturalHeight. These give the true size of the image. If it failed
        // to load, either of these should be zero.
        if (img.naturalWidth === 0) {
            return false;
        }

        // No other way of checking: assume it’s ok.
        return true;
    }

    /**
     *
     * @param img
     * @param callback
     */
    static whenImageLoaded(img,callback) {
        if (img==null) {
            console.log('img is null',img);
            return;
        }
        if (Images.isImageLoaded(img)) {
            callback(img);
        } else {
            if(img.addEventListener) {
                console.log('wait for img.addEventListener load ',img);
                img.addEventListener('load', callback, true);
            } else {
                console.warn("no image eventlistener - callback directly");
                callback();
            }

        }
    }

    /**
     *
     * @param img
     * @returns {Promise<any>}
     */
    static whenImgLoadedPromise(img) {
        if (img == null) return null;
        return new Promise((resolve, reject) => {
            if (Images.isImageLoaded(img)) {
                console.log(`:: isImageLoaded: ${img} is loaded`);
                resolve(img);
            } else {
                img.addEventListener('load', () => resolve(img));
                img.addEventListener('error', () => reject(new Error(`Failed to load image's URL: ${url}`)));
            }
        });
    }

    /**
     * resized ein Image so, dass es in jeder Dimension maximal 1024 Pixel misst, und in jedem Fall ein PowerOfTwo ist, und gibt es als Canvas zurück
     */
    static textureSaneResize(image,maxSize=1024) {
        if (image==null) {
            return null;
        }
        const fit = v => Math.min(maxSize,Maths.nearestPowerOfTwo(v));
        return Images.canvasFromImage(image,fit(image.width),fit(image.height));
    }

}

window.Images = Images;

